/** How many cards to paint before the scroll area has been measured. */
export const HOMEPAGE_CAROUSEL_INITIAL_WINDOW = 6;

/** Extra cards on each side of the visible window. */
export const HOMEPAGE_CAROUSEL_OVERSCAN = 2;

/** Start building a below-fold section this far before it enters the screen. */
export const HOMEPAGE_SECTION_ROOT_MARGIN = '640px 0px';

/** Space kept for a destination section until it is near the screen. */
export const HOMEPAGE_SECTION_PLACEHOLDER_HEIGHT = 600;

export const DESTINATION_CARD_TITLE_FALLBACK = 'International Destination';

export const DESTINATION_CARD_DESCRIPTION_FALLBACK = 'Explore this amazing destination';

export const HOMEPAGE_CAROUSEL_LOADING_LABEL = 'Loading trending destinations...';

/** Featured destination row (Tropical Paradise and any section that reuses it). */
export const FEATURED_DESTINATION_VISIBLE_COUNT = 4;
export const FEATURED_DESTINATION_TABLET_VISIBLE_COUNT = 2;
export const FEATURED_DESTINATION_MOBILE_VISIBLE_COUNT = 1;
export const FEATURED_DESTINATION_TABLET_BREAKPOINT = 1100;
export const FEATURED_DESTINATION_MOBILE_BREAKPOINT = 700;
export const FEATURED_DESTINATION_SCROLL_GAP = 16;
export const FEATURED_DESTINATION_EXPLORE_LABEL = 'Explore Guide';
export const BUCKET_LIST_ICON_COLOR = '#1D4ED8';
export const TROPICAL_PARADISE_ICON_COLOR = '#1493D4';
export const INTO_THE_WILD_ICON_COLOR = '#15803D';
export const HIDDEN_GEMS_ICON_COLOR = '#7C3AED';
export const FEATURED_DESTINATION_ICON_FONT_SIZE = 'large';
export const TROPICAL_PARADISE_TITLE_FALLBACK = 'Tropical Paradise Escapes';
export const TROPICAL_PARADISE_DESCRIPTION_FALLBACK = 'Unwind at stunning beaches, island hideaways, and serene coastal destinations.';
export const BUCKET_LIST_TITLE_FALLBACK = 'Bucket List Journeys';
export const BUCKET_LIST_DESCRIPTION_FALLBACK = 'Experience breathtaking luxury escapes and once-in-a-lifetime destinations.';
export const INTO_THE_WILD_TITLE_FALLBACK = 'Into the Wild';
export const INTO_THE_WILD_DESCRIPTION_FALLBACK = 'Explore scenic mountains, adventure hotspots, and wildlife escapes.';
export const HIDDEN_GEMS_TITLE_FALLBACK = 'Hidden Gems & Offbeat Escapes';
export const HIDDEN_GEMS_DESCRIPTION_FALLBACK = 'Discover underrated destinations and unique cultural treasures.';

/** Explore What's Trending spotlight (one city, hero, and more images). */
export const TRENDING_SPOTLIGHT_TITLE_FALLBACK = 'Explore What\'s Trending';
export const TRENDING_SPOTLIGHT_DESCRIPTION_FALLBACK = 'Discover the world\'s most popular destinations.';
export const TRENDING_SPOTLIGHT_ICON_COLOR = '#111827';
export const TRENDING_SPOTLIGHT_EXPLORE_PREFIX = 'Explore';
export const TRENDING_SPOTLIGHT_MORE_IMAGES_LABEL = 'More Images';
export const TRENDING_SPOTLIGHT_CITY_FALLBACK = 'Destination';
export const TRENDING_SPOTLIGHT_COUNTRY_FALLBACK = 'International';
export const TRENDING_SPOTLIGHT_IMAGE_LIMIT = 8;
export const TRENDING_SPOTLIGHT_THUMBNAIL_COUNT = 4;
export const TRENDING_SPOTLIGHT_ROTATE_MS = 3000;
export const TRENDING_SPOTLIGHT_CROSSFADE_MS = 500;

/** Homepage travel-gallery cards (featured + 2×2). Tighter than the original 16/9 and 4/3 frames. */
export const HOMEPAGE_GALLERY_ROW_GAP = '14px';
export const HOMEPAGE_GALLERY_FEATURED_ASPECT = '2 / 1';
export const HOMEPAGE_GALLERY_TILE_ASPECT = '3 / 2';
export const HOMEPAGE_GALLERY_BADGE_OFFSET = 8;
export const HOMEPAGE_GALLERY_OVERLAY_PADDING = 1.25;
export const HOMEPAGE_GALLERY_TITLE_FEATURED = { xs: '18px', sm: '18px', md: '20px' };
export const HOMEPAGE_GALLERY_TITLE_TILE = { xs: '15px', sm: '15px', md: '17px' };
export const HOMEPAGE_GALLERY_META_SIZE = '11px';
export const HOMEPAGE_GALLERY_TEXT_GAP = 0.25;
export const HOMEPAGE_GALLERY_SERVICES_LABEL = 'Flights • Hotels • Resorts';
export const HOMEPAGE_GALLERY_TITLE_FALLBACK = 'Destination';
export const HOMEPAGE_GALLERY_CATEGORY_FALLBACK = 'Travel Destination';
export const HOMEPAGE_GALLERY_FEATURED_CATEGORY_FALLBACK = 'Trending Trips Near You';
export const HOMEPAGE_GALLERY_TILE_COUNT = 4;
export const HOMEPAGE_GALLERY_FEATURED_IMAGE_SIZE = { width: 652, height: 367 };
export const HOMEPAGE_GALLERY_TILE_IMAGE_SIZE = { width: 314, height: 236 };

const CARD_BASE_SX = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  flex: '0 0 auto',
  boxSizing: 'border-box',
  alignSelf: 'flex-start',
  cursor: 'pointer',
};

/** Section 2 (bucket list) uses a percentage width. Sections 3–5 use a fixed width. */
export const DESTINATION_CARD_LAYOUTS = {
  percent: {
    ...CARD_BASE_SX,
    gap: '16px',
    width: { xs: 'calc(25% - 15px)', sm: 'calc(25% - 15px)', md: 'calc(26% - 15px)', lg: 'calc(25% - 15px)' },
    minWidth: { xs: '160px', sm: '160px', md: '160px', lg: '220px' },
    maxWidth: { xs: 'calc(25% - 15px)', sm: 'calc(25% - 15px)', md: 'calc(26% - 15px)', lg: 'calc(25% - 15px)' },
  },
  fixed: {
    ...CARD_BASE_SX,
    gap: '10px',
    width: { xs: '180px', sm: '180px', md: '220px', lg: 'calc(25% - 15px)' },
    minWidth: { xs: '180px', sm: '180px', md: '220px' },
    maxWidth: { xs: '180px', sm: '180px', md: '220px', lg: 'calc(25% - 15px)' },
  },
};

export const DESTINATION_CARD_IMAGE_SX = {
  width: '100%',
  aspectRatio: '1 / 1',
  alignSelf: 'stretch',
  borderRadius: '12px',
  objectFit: 'cover',
  objectPosition: 'center',
};

export const DESTINATION_CARD_TITLE_SX = {
  alignSelf: 'stretch',
  color: '#000',
  fontSize: { xs: '14px', sm: '14px', md: '16px' },
  fontWeight: 700,
  lineHeight: 1.3,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
};

export const DESTINATION_CARD_DESCRIPTION_SX = {
  alignSelf: 'stretch',
  color: '#6C6C6C',
  fontSize: { xs: '12px', sm: '12px', md: '14px' },
  fontWeight: 400,
  opacity: 0.75,
  lineHeight: 1.3,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
};

export const HOMEPAGE_CAROUSEL_LOADING_SX = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '200px',
  color: '#6C6C6C',
};
